import { useCallback, useEffect, useMemo, useState } from "react";
import ServerSideDataTable from "./ServerSideDataTable";
import { getShops } from "../api";

const DEFAULT_SORT_BY = "id";
const DEFAULT_SORT_INDEX = 0;

const FILTER_BADGES = [
    { key: "geometry",       label: "Géométrie",          testTypeId: 1 },
    { key: "tire_structure", label: "Structure de pneu",  testTypeId: 2 },
];

// Clés valides pour éviter de polluer l'URL avec des valeurs arbitraires
const VALID_FILTER_KEYS = new Set(FILTER_BADGES.map((f) => f.key));

const STORAGE_KEY = "test_types_filter";

// lecture initiale depuis l'URL
function readFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("filters");
    if (!raw) return null;
    return raw.split(",").filter((k) => VALID_FILTER_KEYS.has(k));
}

function readFiltersFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((k) => VALID_FILTER_KEYS.has(k));
    } catch {
        return [];
    }
}

// Priorité : URL (si présente) > localStorage > []
function readInitialFilters() {
    const fromUrl = readFiltersFromUrl();
    if (fromUrl !== null) return fromUrl;
    return readFiltersFromStorage();
}

function ShopsDataTable() {
    const [activeFilters, setActiveFilters] = useState(readInitialFilters());

    const toggleFilter = useCallback((key) => {
        setActiveFilters((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    }, []);

    useEffect(() => {
        // URL
        const params = new URLSearchParams(window.location.search);
        if (activeFilters.length > 0) {
            params.set("filters", activeFilters.join(","));
        } else {
            params.delete("filters");
        }
        const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
        window.history.replaceState(null, "", next);

        // localStorage
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(activeFilters));
        } catch (err) {
            // quota dépassé ou mode privé restrictif — on ignore silencieusement
        }
    }, [activeFilters]);

    // Dérive testTypeId depuis la sélection :
    // - aucun ou les deux sélectionnés → null (pas de filtre)
    // - un seul → testTypeId correspondant
    const testTypeId = useMemo(() => {
        const active = FILTER_BADGES.filter((f) => activeFilters.includes(f.key));
        return active.length === 1 ? active[0].testTypeId : null;
    }, [activeFilters]);

    const columns = useMemo(() => [
        { data: "id", title: "ID" },
        {
            data: "test_type_ids",
            title: "Contrôle",
            render: (data) => {
                let htmlBadges = "";
                if (Array.isArray(data)) {
                    htmlBadges = data.map((testTypeId) => {
                        const badge = FILTER_BADGES.find((f) => f.testTypeId === testTypeId);
                        return `<span class="badge rounded-pill border-0 fs-6 fw-normal px-3 py-2 bg-body-secondary text-body me-2">${badge ? badge.label : "-"}</span>`;
                    }).join("");
                }
                return htmlBadges;
            }
        },
        {
            data: "email",
            title: "Email",
            render: (data) => data || "-"
        },
        {
            data: "name",
            title: "Nom",
            render: (data) => data || "-"
        },
        {
            data: "address",
            title: "Adresse",
            render: (data) => data || "-"
        },
        {
            data: "phone",
            title: "Téléphone",
            render: (data) => data || "-"
        },
        {
            data: "principle",
            title: "Responsable",
            render: (data) => data || "-"
        },
        {
            data: "id",
            title: "Action",
            orderable: false,
            searchable: false,
            render: (data, type) => {
                if (type !== "display") {
                    return data || "";
                }

                return `<a href="/shops/${data}" class="btn btn-outline-primary btn-sm">Détail</a>`;
            }
        }
    ], []);

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-3">
                {FILTER_BADGES.map(({ key, label }) => {
                    const active = activeFilters.includes(key);
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => toggleFilter(key)}
                            aria-pressed={active}
                            className={`badge rounded-pill border-0 fs-6 fw-normal px-3 py-2 ${
                                active
                                    ? "bg-primary text-white"
                                    : "bg-body-secondary text-body"
                            }`}
                            style={{ cursor: "pointer" }}
                        >
                            {active && <i className="bi bi-check me-1" aria-hidden="true" />}
                            {label}
                        </button>
                    );
                })}
            </div>

            {/*
                key={testTypeId} : quand testTypeId change, React démonte et remonte
                ServerSideDataTable, ce qui force DataTables à refaire un ajax
                avec les nouveaux params — sans toucher à ServerSideDataTable.
            */}
            <ServerSideDataTable
                key={testTypeId}
                columns={columns}
                defaultSortBy={DEFAULT_SORT_BY}
                defaultSortIndex={DEFAULT_SORT_INDEX}
                errorMessage="Impossible de charger les ateliers pour l'instant."
                fetchPage={(token, query) => getShops(token, query.page, query.pageSize, {
                    q: query.q,
                    sortBy: query.sortBy,
                    sortDir: query.sortDir,
                    // --- NOUVEAU : injecté dans la requête, absent si null ---
                    ...(testTypeId !== null && { testTypeId })
                })}
            />
        </div>);
}

export default ShopsDataTable;
